import React from 'react'
import ReactDOMServer from 'react-dom/server'
import mapboxgl from 'mapbox-gl'
import './App.css'

import 'spectre.css'

import locationsImport from './locations.json'
import connectionsImport from './connections.json'

import {Overlay} from './overlay'
import {BookDescription} from './book_description'
import {Faq} from './faq'
import {Imprint} from './imprint'
import {IconLook} from './icon_look'
import {Short} from './short'
import {Legend} from './legend'

mapboxgl.accessToken = 'pk.eyJ1IjoibmV0endlcmsiLCJhIjoiY2pwNDQ5dG9rMG90ZjNsbW51bjJ1Ym1kcyJ9.IxXsfzqvl5chXzGQU5r3GQ'

const EMPTY_GEOJSON = { generateId: true, type: 'geojson', data: { type: 'FeatureCollection', features: [] } }

// Manual polyfill
if (!Object.entries)
  Object.entries = function (obj) {
    var ownProps = Object.keys(obj),
      i = ownProps.length,
      resArray = new Array(i); // preallocate the Array
    while (i--)
      resArray[i] = [ownProps[i], obj[ownProps[i]]];

    return resArray;
  };

class App extends React.Component {
  state = {
    locations: {},
    connections: [],
    locationsLoading: true,
    connectionsLoading: true,
    mapLoading: true,
    overlayLocation: null,
    overlayLocations: [],
    isIframe: !!document.location.href.match(/iframe/)
  }

  componentDidUpdate() {
    if (this.state.locationsLoading || this.state.connectionsLoading || this.state.mapLoading) {
      // console.log('isloading');
    } else {
      this.updateSources()
    }
  }

  storeLocations = (snap) => {
    let locations = {}
    snap.forEach((location) => { locations[location.id] = location })
    this.setState({locations, locationsLoading: false})
  }

  storeConnections = (snap) => {
    this.setState({connections: connectionsImport, connectionsLoading: false})
  }

  updateSources = () => {
    let geojsonDataLocations = { type: 'FeatureCollection', features: [] }

    Object.entries(this.state.locations).forEach(([id, location]) => {
      const [lat, long] = location.latlng.split(',')
      geojsonDataLocations.features.push({
        type: 'Feature',
        id: location.id,
        geometry: { type: 'Point', coordinates: [long, lat] },
        properties: {
          id: location.id,
          name: (location.name || ""),
          description: (location.description || ""),
          icon: location.icon
        }
      })
    })
    this.map.getSource('locations').setData(geojsonDataLocations)

    let geojsonDataConnections = { type: 'FeatureCollection', features: [] }

    this.state.connections.forEach((connection) => {
      const from_id = connection.from_id
      const to_id   = connection.to_id
      const from    = this.state.locations[from_id]
      const to      = this.state.locations[to_id]

      if (from && to) {
        const from_coords = from.latlng.split(',').reverse()
        const to_coords = to.latlng.split(',').reverse()
        geojsonDataConnections.features.push({
          type: 'Feature',
          properties: { from: from_id, to: to_id },
          geometry: {
            type: 'LineString',
            coordinates: [ from_coords, to_coords ]
          }
        })
      }
    })

    this.map.getSource('connections').setData(geojsonDataConnections)
  }

  buildMap = () => {
    let map = new mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/netzwerk/cjsz38rjg14ot1fqclictjg9o',
      center: [9.51468, 51.2371],
      minZoom: 4.7,
      maxZoom: 12,
      zoom: 4.7
    })
    map.scrollZoom.disable() // only mobile?
    return map
  }

  addMapLayers = () => {
    this.map.addSource('locations', EMPTY_GEOJSON)
    this.map.addSource('connections', EMPTY_GEOJSON)

    this.setState({mapLoading: false})

    this.map.addLayer({
      id: 'connections-highlighted',
      type: 'line',
      source: 'connections',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'black',
        'line-width': 2,
        'line-opacity': 0.6,
      },
      filter: ['==', 'from', '']
    })

    this.map.addLayer({
      id: 'locations',
      type: 'circle',
      source: 'locations',
      paint: {
        'circle-radius': 9,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'black',
        'circle-opacity': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
            1.0,
            0.5
        ],
        'circle-color': [
          'match',
          ['get', 'icon'],
          'thinktank', IconLook.thinktank.color,
          'medium', IconLook.medium.color,
          'partei', IconLook.partei.color,
          'verlag', IconLook.verlag.color,
          'finanzier', IconLook.finanzier.color,
          'kampagne', IconLook.kampagne.color,
          'white' // default
        ],
      }
    })
  }

  handleMapClick = () => {
    if (this.state.overlayLocation || this.state.overlayLocations.length > 0) {
      this.map.setFilter('connections-highlighted', ['in', 'from', ''])
      this.setState({overlayLocation: null, overlayLocations: []})
      this.popup.remove()
    }
  }

  switchOverlay = (id) => {
    const location = this.state.locations[id]

    let lat, long
    [lat, long] = location.latlng.split(',')
    this.map.panTo([long, lat])

    const connectedLocations = this.state.connections
      .filter(c => ((c.from_id === id) || (c.to_id === id)))
      .map(c => this.state.locations[(c.from_id === id ? c.to_id : c.from_id)])
    this.map.setFilter('connections-highlighted', ['any', ['==', 'from', id], ['==', 'to', id]])

    this.setState({overlayLocation: location, overlayLocations: connectedLocations})
  }

  showByType = (type) => {
    const locations = Object.values(this.state.locations).filter(l => l.icon === type)
    this.setState({overlayLocation: null, overlayLocations: locations})
  }

  handleMarkerClick = (event) => {
    this.switchOverlay(event.features[0].properties.id)
  }

  switchPopup = (id) => {
    const location = this.state.locations[id]
    const [lat, long] = location.latlng.split(',')
    const popupContent = ReactDOMServer.renderToString(<Short location={location} />)
    this.popup.setLngLat([long, lat]).setHTML(popupContent).addTo(this.map)
  }

  handleMouseEnter = (event) => {
    // Change the cursor style as a UI indicator.
    this.map.getCanvas().style.cursor = 'pointer'

    const id = event.features[0].properties.id

    this.map.setFeatureState({source: 'locations', id: event.features[0].id}, {hover: true})
    this.setState({hoverFeatureId: event.features[0].id})

    this.switchPopup(id)
  }

  handleMouseLeave = (event) => {
    this.map.getCanvas().style.cursor = ''

    const {hoverFeatureId} = this.state
    if (hoverFeatureId) {
      this.map.setFeatureState({source: 'locations', id: hoverFeatureId}, {hover: false})
      this.setState({hoverFeatureId: null})
    }

    if (!this.state.overlayLocation) {
      this.popup.remove()
    }
  }

  componentDidMount() {
    this.storeLocations(locationsImport)
    this.storeConnections(connectionsImport)
    this.map = this.buildMap()
    this.popup = new mapboxgl.Popup({closeButton: false, closeOnClick: false})
    this.map.on('load', this.addMapLayers)
    this.map.on('click', this.handleMapClick)
    this.map.on('click', 'locations', this.handleMarkerClick)
    this.map.on('mouseenter', 'locations', this.handleMouseEnter)
    this.map.on('mouseleave', 'locations', this.handleMouseLeave)

    var nav = new mapboxgl.NavigationControl();
    this.map.addControl(nav, 'top-right');
  }

  componentWillUnmount() {
    this.map.remove()
  }

  render() {
    if (this.state.isIframe) {
      document.body.style.overflow = 'hidden'
      if (!document.body.className.match(/iframe/)) {
        document.body.className += ' ' + 'ndnr-iframe'
      }

      return <div>
        <div className="columns col-gapless">
          <div className='column col-12 ndnr-map' ref={el => this.mapContainer = el} />
          <Overlay
            location={this.state.overlayLocation}
            connectedLocations={this.state.overlayLocations}
            onClose={this.handleMapClick}
            switchOverlay={this.switchOverlay}
            switchPopup={this.switchPopup}
          />
        </div>
      </div>
    }

    return <div>
      <BookDescription />
      <ScrollTo selector=".ndnr-map" />

      <div className="container">
        <div className="columns col-gapless">
          <div className='column col-12 ndnr-map' ref={el => this.mapContainer = el} />
          <Overlay
            location={this.state.overlayLocation}
            connectedLocations={this.state.overlayLocations}
            onClose={this.handleMapClick}
            switchOverlay={this.switchOverlay}
            switchPopup={this.switchPopup}
          />
        </div>
      </div>

      <div className="container">
        <div className="columns col-gapless">
          <Legend showByType={this.showByType} />
        </div>
      </div>

      <ScrollTo selector=".ndnr-faq" />
      <Faq />

      <ScrollTo selector=".ndnr-imprint" />
      <Imprint />
      <ScrollTo selector=".ndnr-map" inverted />
    </div>
  }
}

const ScrollTo = ({selector, inverted}) => {
  return <a
    style={{textAlign: 'center'}}
    className='c-hand d-block text-gray mb-2'
    onClick={() => document.querySelector(selector).scrollIntoView({behavior: 'smooth'})}
  >
    <i className={`fas fa-chevron-${inverted ? 'up' : 'down'} fa-3x`}></i>
  </a>
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js